import React, { useEffect } from "react";
import { MapContainer, TileLayer, Marker, Popup, Polyline } from "react-leaflet";
import L from "leaflet";
import { useI18n } from "@/i18n";

// Fix default marker icons in webpack
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

// Custom branded icon
const brandIcon = L.divIcon({
  className: "custom-pin",
  html: `<div style="width:14px;height:14px;border-radius:9999px;background:#0A2463;border:3px solid #C5A059;box-shadow:0 4px 12px rgba(10,36,99,0.4)"></div>`,
  iconSize: [14, 14],
  iconAnchor: [7, 7],
});

const airportIcon = L.divIcon({
  className: "custom-pin",
  html: `<div style="width:18px;height:18px;border-radius:9999px;background:#C5A059;border:3px solid #0A2463;box-shadow:0 4px 12px rgba(10,36,99,0.5)"></div>`,
  iconSize: [18, 18],
  iconAnchor: [9, 9],
});

export default function TerritoryMap({ pois }) {
  const { lang, t } = useI18n();

  if (!pois || pois.length === 0) return null;

  // Center on Gallura (between Olbia and Santa Teresa)
  const center = [41.05, 9.4];

  return (
    <section
      id="territory"
      data-testid="territory-section"
      className="py-24 lg:py-32 bg-[#0F172A] text-white"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="max-w-2xl">
          <p className="text-xs uppercase tracking-[0.3em] accent-text">{t.territory.eyebrow}</p>
          <h2 className="font-display text-4xl md:text-5xl mt-4 leading-tight text-white">
            {t.territory.title}
          </h2>
          <p className="text-base md:text-lg text-white/70 mt-5">{t.territory.lead}</p>
        </div>

        <div className="mt-12 rounded-2xl overflow-hidden border border-white/10 shadow-2xl">
          <MapContainer
            center={center}
            zoom={9}
            style={{ height: 520, width: "100%" }}
            scrollWheelZoom={false}
            data-testid="leaflet-map"
          >
            <TileLayer
              attribution='&copy; OpenStreetMap contributors &copy; CARTO'
              url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
            />
            {pois.map((p) => {
              const name = lang === "it" ? p.name_it : p.name_en;
              const icon = p.type === "airport" ? airportIcon : brandIcon;
              return (
                <Marker key={p.id} position={[p.lat, p.lng]} icon={icon}>
                  <Popup>
                    <div className="font-display brand-text text-base">{name}</div>
                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${p.lat},${p.lng}`}
                      target="_blank"
                      rel="noreferrer"
                      className="text-xs underline accent-text"
                      data-testid={`google-maps-link-${p.id}`}
                    >
                      Apri in Google Maps
                    </a>
                  </Popup>
                </Marker>
              );
            })}
          </MapContainer>
        </div>

        <div className="mt-10 flex flex-wrap gap-3">
          {pois.map((p) => (
            <span
              key={p.id}
              className="px-4 py-2 rounded-full bg-white/5 border border-white/10 text-sm text-white/80"
            >
              {lang === "it" ? p.name_it : p.name_en}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}
